/**
 * 用户行为埋点 — 极简版。
 *
 * 设计：
 * - 内存 queue + 5s 攒发 + sendBeacon flush 残余（页面卸载时）
 * - 隐私：只记 path + action key + payload 不含敏感词
 * - 不阻塞页面渲染；有界队列，网络失败丢弃，不影响业务操作
 * - 身份切换清队列；卸载按批发送，仅 sendBeacon 接受后移除
 */

interface QueuedEvent {
  event_type: string;
  target: string;
  referrer?: string | null;
  payload?: Record<string, unknown> | null;
  client_ts: string;
}

const ENDPOINT = "/api/v1/usage/events";
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH = 50;
let queue: QueuedEvent[] = [];
let flushTimer: number | null = null;
let lastReferrer: string | null = null;
let userId: number | null = null;
let epoch = 0;
let inFlight = false;

/** 身份切换立即丢弃旧用户待发事件，禁止跨用户补报。 */
export function setUsageUser(id: number | null): void {
  if (userId === id) return;
  userId = id;
  epoch++;
  queue = [];
  lastReferrer = null;
  if (flushTimer != null) window.clearTimeout(flushTimer);
  flushTimer = null;
}

export function usageGeneration(): number { return epoch; }

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = window.setTimeout(flush, FLUSH_INTERVAL_MS);
}

export async function flushUsage(): Promise<void> {
  if (flushTimer != null) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0 || userId == null || inFlight) return;
  inFlight = true;
  const generation = epoch;
  const batch = queue.splice(0, MAX_BATCH);
  try {
    const response = await fetch(ENDPOINT, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
    if (response.ok && generation === epoch) window.dispatchEvent(new Event("whaleseek:usage-flushed"));
  } catch {
    // 失败丢弃（自用产品，不做严格保证）
  }
  inFlight = false;
  if (queue.length > 0) scheduleFlush();
}

const flush = flushUsage;

export function track(
  type: string,
  target: string,
  payload?: Record<string, unknown> | null,
): void {
  if (!type || !target || userId == null) return;
  if (queue.length >= 200) queue.shift();
  queue.push({
    event_type: type,
    target: target.slice(0, 256),
    referrer: type === "page_view" ? lastReferrer : null,
    payload: payload ?? null,
    client_ts: new Date().toISOString(),
  });
  if (queue.length >= MAX_BATCH) {
    void flush();
  } else {
    scheduleFlush();
  }
}

/** 路由切换调用：记 page_view 并维护 referrer */
export function trackPageView(path: string): void {
  if (!path || path === lastReferrer) return;
  track("page_view", path);
  lastReferrer = path;
}

if (typeof window !== "undefined") {
  // 离开 / 后台切走：尽量用 sendBeacon 把 queue 清空
  const beaconFlush = () => {
    if (queue.length === 0) return;
    if (userId == null) return;
    while (queue.length > 0) {
      const batch = queue.slice(0, MAX_BATCH);
      const body = new Blob([JSON.stringify({ events: batch })], { type: "application/json" });
      if (!navigator.sendBeacon(ENDPOINT, body)) { scheduleFlush(); break; }
      queue.splice(0, batch.length);
    }
  };
  window.addEventListener("pagehide", beaconFlush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") beaconFlush();
  });
}
